// React/finance-app/src/features/gateway-management/UserGatewayPage.jsx
/**
 * User Gateway Management Page
 * New tab for managing gateway ports
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api, {
  checkGatewayAccess,
  getMyGateway,
  getMyGatewayPorts,
  createMyGatewayPort,
  updateMyGatewayPort,
  deleteMyGatewayPort,
  updateAdvicePasswordMe,
  fetchAdvicePasswordsMe,
  fetchPortsMe,
  fetchMe,
  fetchUniqueSites,
  getAssignableUsers,
  listMyGateways,
  syncPortsFromHardware,
  fetchMessagesIndexedByPort,
  requestSimConversion,
  requestSimConversionBulk,
} from '../../api';
import GatewayPortsTable from './components/GatewayPortsTable';
import PortStatusMonitorPanel from './components/PortStatusMonitorPanel';
import ConvertToTtOtpModal from '../user-dashboard/components/ConvertToTtOtpModal';
import usePortsByEmail from '../user-dashboard/hooks/usePortsByEmail';
import MessageCarousel from '../user-dashboard/components/MessageCarousel';
import {
  validatePortNumber as validatePortNumberUtil,
  cleanPortNumberInput,
  formatPortNumber,
} from './utils/portValidation';
import usePortActivation from '../user-dashboard/hooks/usePortActivation';
import useSmartActivationPolling from '../user-dashboard/hooks/useSmartActivationPolling';
import {
  PortActivationBadge,
  getActivationButtonState,
  isSimConvertedYes,
  shouldShowActivate,
  canRequestSimConversion,
} from '../../shared/portActivation';

function PollingRunner({
  portId,
  gatewayId,
  portNumber,
  setPortStatusFromActivity,
  stopActivationPolling,
  refreshActivation,
}) {
  const polling = useSmartActivationPolling({
    portId,
    gatewayId,
    portNumber,
    onStatusChange: (data) => {
      setPortStatusFromActivity({
        [portId]: {
          status: data.status,
          signal: data.signal,
          operator: data.operator,
          timestamp: Date.now(),
        },
      });
    },
    onComplete: () => {
      stopActivationPolling(portId);
      refreshActivation();
    },
  });

  useEffect(() => {
    polling.start();
    return () => polling.stop();
  }, []);

  return null;
}

function UserGatewayPage() {
  const [gateway, setGateway] = useState(null);
  const [gatewayPorts, setGatewayPorts] = useState([]);
  const [advice, setAdvice] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [editingPort, setEditingPort] = useState(null);
  const [showAddForm, setShowAddForm] = useState(true); // Always open by default
  const [formData, setFormData] = useState({
    port_number: '',
    assigned_number: '',
    network_provider: '',
    target_user_code: '', // Optional: assign to another user
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastCreatedPort, setLastCreatedPort] = useState(null);
  const [testStatus, setTestStatus] = useState(null); // null, 'sent', 'confirmed', 'failed' (for form)
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [portTestStatus, setPortTestStatus] = useState({}); // Map of portId -> test status
  const [portActivationStatus, setPortActivationStatus] = useState({}); // Map of portId -> activation status ('not_activated', 'activating', 'active', 'queued')
  const [showSimGuide, setShowSimGuide] = useState(false); // Toggle for SIM insertion guide
  const [pendingTestPorts, setPendingTestPorts] = useState([]); // Ports waiting for test confirmation
  const [sites, setSites] = useState([]); // List of available sites
  const [portNumberError, setPortNumberError] = useState(null); // Port number validation error
  const [assignableUsers, setAssignableUsers] = useState([]); // Users that can be assigned ports (have slack_id)
  const [targetUserCode, setTargetUserCode] = useState(''); // Target user_code for port assignment
  const [currentUserCode, setCurrentUserCode] = useState(''); // Current logged-in user's user_code (gateway owner)
  const [portStatusMonitorOpen, setPortStatusMonitorOpen] = useState(true); // open by default so users see port status on My Sim Box
  const [hardwareStatusArray, setHardwareStatusArray] = useState([]); // Live hardware port statuses from Port Status Monitor
  const [bulkConversionOpen, setBulkConversionOpen] = useState(false);
  const [assignedPorts, setAssignedPorts] = useState([]);

  const portsByEmail = usePortsByEmail(gatewayPorts);
  const eligibleConversionPorts = useMemo(() => {
    const byId = new Map();
    for (const p of gatewayPorts || []) {
      if (p?.id != null) byId.set(p.id, p);
    }
    for (const p of assignedPorts || []) {
      if (p?.id != null && !byId.has(p.id)) byId.set(p.id, p);
    }
    return [...byId.values()].filter((p) => canRequestSimConversion(p));
  }, [gatewayPorts, assignedPorts]);
  const hasLoadedRef = useRef(false);

  const [portMessages, setPortMessages] = useState({});

  const {
    portStatus,
    portCountdowns,
    clearPortMessage,
    onActivatePort,
    setPortStatusFromActivity,
    startCountdown,
    pollingPorts,
    stopActivationPolling,
    refreshActivation,
  } = usePortActivation(gatewayPorts, setGatewayPorts, {
    scope: 'me',
    gatewayId: gateway?.id ?? null,
    adaptivePoll: true,
  });

  // Hardware sync
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const syncingRef = useRef(false);
  
  // Helper to convert error details to string (handles arrays and objects)
  const formatErrorDetail = useCallback((detail) => {
    if (!detail) return null;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      // Handle FastAPI validation errors: [{type, loc, msg, input}, ...]
      return detail.map(err => {
        if (typeof err === 'string') return err;
        if (err.msg) return `${err.loc?.join('.') || ''}: ${err.msg}`;
        return JSON.stringify(err);
      }).join('; ');
    }
    if (typeof detail === 'object') {
      // Handle object errors
      if (detail.message) return detail.message;
      if (detail.msg) return detail.msg;
      return JSON.stringify(detail);
    }
    return String(detail);
  }, []);

  const handleActivatePort = useCallback(
    async (portId, phoneNumber) => {
      const portIdInt = typeof portId === 'string' ? parseInt(portId, 10) : portId;
      if (isNaN(portIdInt)) {
        setError('Invalid port ID');
        return;
      }

      setPortActivationStatus((prev) => ({ ...prev, [portIdInt]: 'activating' }));

      try {
        await onActivatePort(portIdInt, phoneNumber);

        syncPortsFromHardware()
          .catch(() => {})
          .then(() => {
            setLastSyncTime(new Date());
            refreshActivation();
          });

        try {
          setError(null);
          const data = await getMyGatewayPorts();
          setGatewayPorts(data);

          if (data && data.length > 0) {
            const messageMap = await fetchMessagesIndexedByPort(data, 10);
            setPortMessages((prev) => ({ ...prev, ...messageMap }));
          }
        } catch (fetchErr) {
          console.error('Failed to refresh ports after activation:', fetchErr);
        }
      } catch (err) {
        const errorDetail =
          formatErrorDetail(err.response?.data?.detail) || err.message || 'Activation failed';
        setError(errorDetail);
        setPortActivationStatus((prev) => ({ ...prev, [portIdInt]: 'not_activated' }));
      }
    },
    [onActivatePort, formatErrorDetail, refreshActivation]
  );

  const getButtonState = useCallback(
    (port) => {
      const state = getActivationButtonState(port, portStatus);
      if (state.text === 'Activating…') {
        return {
          ...state,
          text: (
            <>
              <span className="spinner-border spinner-border-sm me-1" />
              Activating…
            </>
          ),
        };
      }
      return state;
    },
    [portStatus]
  );

  const getStatusBadge = useCallback(
    (port) => {
      const portIdNum = port?.id != null ? Number(port.id) : null;
      const countdown =
        portIdNum != null
          ? portCountdowns[portIdNum] ?? portCountdowns[port.id]
          : null;
      return (
        <PortActivationBadge
          statusEntry={portStatus[port?.id] || {}}
          countdown={countdown}
        />
      );
    },
    [portStatus, portCountdowns]
  );

  const fetchGateway = useCallback(async () => {
    try {
      // Fetch user's gateways using user-specific endpoint (require_user, not staff)
      const userGateways = await listMyGateways();

      // Use first gateway or null if none
      if (userGateways && userGateways.length > 0) {
        setGateway(userGateways[0]);
      } else {
        setGateway(null);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching gateway:', err);
      const errorDetail = formatErrorDetail(err.response?.data?.detail) || err.message || 'Failed to fetch gateway';
      setError(errorDetail);
      setGateway(null);
    }
  }, [formatErrorDetail]);

  const fetchPorts = useCallback(async () => {
    try {
      setError(null);
      const [boxResult, mineResult] = await Promise.allSettled([
        getMyGatewayPorts(),
        fetchPortsMe({ skipCache: true }),
      ]);
      const boxPorts = boxResult.status === 'fulfilled' ? boxResult.value : [];
      const mine = mineResult.status === 'fulfilled' ? mineResult.value : [];
      if (boxResult.status === 'rejected') {
        const err = boxResult.reason;
        const errorDetail = formatErrorDetail(err?.response?.data?.detail) || err?.message || 'Failed to fetch ports';
        setError(errorDetail);
      }
      setGatewayPorts(boxPorts || []);
      setAssignedPorts(Array.isArray(mine) ? mine : []);

      // Load messages for all ports
      if (boxPorts && boxPorts.length > 0) {
        const messageMap = await fetchMessagesIndexedByPort(boxPorts, 10);
        setPortMessages(prev => ({ ...prev, ...messageMap }));
      }
    } catch (err) {
      const errorDetail = formatErrorDetail(err.response?.data?.detail) || err.message || 'Failed to fetch ports';
      setError(errorDetail);
    }
  }, [formatErrorDetail]);

  const loadAssignedPortsForConversion = useCallback(async () => {
    try {
      const mine = await fetchPortsMe({ skipCache: true });
      setAssignedPorts(Array.isArray(mine) ? mine : []);
    } catch (err) {
      console.error('Failed to load assigned ports for conversion', err);
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    try {
      const [a, sitesList, assignable, userData] = await Promise.all([
        fetchAdvicePasswordsMe().catch(() => []),
        fetchUniqueSites().catch(() => []),
        getAssignableUsers().catch(() => []),
        fetchMe().catch(() => null),
      ]);
      setAdvice(a || []);
      setSites(sitesList || []);
      setAssignableUsers(assignable || []);
      // Get current user's user_code (gateway owner)
      if (userData && userData.user_code) {
        setCurrentUserCode(userData.user_code);
      } else {
        // Fallback to localStorage
        const storedUserCode = localStorage.getItem('user_code') || '';
        setCurrentUserCode(storedUserCode);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      // Fallback to localStorage
      const storedUserCode = localStorage.getItem('user_code') || '';
      setCurrentUserCode(storedUserCode);
    }
  }, []);

  // Load messages for pending test ports
  const loadPendingPortMessages = useCallback(async () => {
    if (pendingTestPorts.length === 0) return;
    
    const indexed = await fetchMessagesIndexedByPort(pendingTestPorts, 10);
    setPortMessages((prev) => ({ ...prev, ...indexed }));
  }, [pendingTestPorts]);

  useEffect(() => {
    // Only load once
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const loadData = async () => {
      try {
        setLoading(true);
        const access = await checkGatewayAccess();
        setHasAccess(access.has_access);
        if (access.has_access) {
          await Promise.all([
            fetchGateway().catch(err => {
              console.log('Gateway fetch:', err.response?.status === 404 || err.response?.status === 422 ? 'No gateway yet' : err);
            }),
            fetchPorts(),
            fetchAllData(),
          ]);
          syncPortsFromHardware().catch(() => {}).then(() => {
            setLastSyncTime(new Date());
            refreshActivation().catch(() => {});
          });
        }
      } catch (err) {
        const errorDetail = formatErrorDetail(err.response?.data?.detail) || err.message || 'Failed to check access';
        setError(errorDetail);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Load messages for pending test ports when they change
  useEffect(() => {
    if (pendingTestPorts.length > 0) {
      loadPendingPortMessages();
    }
  }, [pendingTestPorts, loadPendingPortMessages]);

  useEffect(() => {
    setPortActivationStatus((prev) => {
      const updated = { ...prev };
      pendingTestPorts.forEach((port) => {
        if (port.id && portStatus[port.id]) {
          const status = portStatus[port.id].status;
          if (status === 'active' && updated[port.id] !== 'active') {
            updated[port.id] = 'active';
          } else if (
            (status === 'queued' || status === 'activating') &&
            updated[port.id] === 'not_activated'
          ) {
            updated[port.id] = status;
          }
        }
      });
      return updated;
    });
  }, [portStatus, pendingTestPorts]);

  const handleSyncFromHardware = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    setSyncing(true);
    try {
      await syncPortsFromHardware();
      setLastSyncTime(new Date());
      await refreshActivation();
    } catch {
      // non-fatal
    } finally {
      setSyncing(false);
      syncingRef.current = false;
    }
  }, [refreshActivation]);

  useEffect(() => {
    Object.entries(portStatus).forEach(([id, s]) => {
      const pid = Number(id);
      const hasCountdown = portCountdowns[pid] != null || portCountdowns[id] != null;
      if (s?.status === 'active' && s?.show_30s_warning && !hasCountdown) {
        const secs = Math.max(5, s?.remaining_seconds ?? 30);
        startCountdown(pid, secs);
      }
    });
  }, [portStatus, portCountdowns, startCountdown]);

  const prevCountdownsRef = useRef(portCountdowns);
  useEffect(() => {
    const prev = prevCountdownsRef.current;
    const finishedIds = [];
    Object.keys(prev).forEach((pid) => {
      if (!(pid in portCountdowns)) finishedIds.push(Number(pid));
    });
    if (finishedIds.length > 0) {
      const needsRefresh = finishedIds.some((pid) => {
        const s = portStatus[pid];
        return s?.status === 'active' && s?.show_30s_warning === true;
      });
      if (needsRefresh) setTimeout(() => refreshActivation(), 300);
    }
    prevCountdownsRef.current = portCountdowns;
  }, [portCountdowns, portStatus, refreshActivation]);

  useEffect(() => {
    if (!hasAccess) return;

    const unsubActivation = api.subscribeTo('activation_update', (data) => {
      if (data?.port_id && pendingTestPorts.some((p) => p.id === data.port_id)) {
        if (data.status === 'active' || data.status === 'processing') {
          setPortActivationStatus((prev) => ({ ...prev, [data.port_id]: 'active' }));
        } else if (data.status === 'queued' || data.status === 'activating') {
          setPortActivationStatus((prev) => ({ ...prev, [data.port_id]: data.status }));
        }
      }
    });

    const unsubMessage = api.subscribeTo('new_message', (messageData) => {
      const portInfo = messageData.port_info;
      if (!portInfo) return;

      const allPorts = [...gatewayPorts, ...pendingTestPorts];
      const matchingPort = allPorts.find(
        (p) =>
          p.port_number === portInfo.port_number ||
          p.id === portInfo.port_id ||
          (portInfo.gateway_id &&
            p.gateway_id === portInfo.gateway_id &&
            p.port_number === portInfo.port_number)
      );

      if (!matchingPort?.id) return;
      const portId = matchingPort.id;

      if (!messageData.is_outgoing) {
        setPortStatusFromActivity({
          [portId]: {
            status: 'active',
            timestamp: Date.now(),
            remaining_seconds: 60,
            message_triggered: true,
          },
        });
        startCountdown(portId, 60);

        if (pendingTestPorts.some((p) => p.id === portId)) {
          setPortActivationStatus((prev) => ({ ...prev, [portId]: 'active' }));
        }

        setTimeout(() => refreshActivation(), 5500);
      }

      setPortMessages((prev) => {
        const existing = prev[portId] || [];
        const newMessage = {
          id: messageData.id,
          sender: messageData.sender,
          receiver: messageData.receiver,
          message: messageData.message,
          timestamp: messageData.timestamp,
          is_outgoing: messageData.is_outgoing || false,
          is_delivery_report: messageData.is_delivery_report || false,
        };
        return { ...prev, [portId]: [newMessage, ...existing].slice(0, 10) };
      });
    });

    return () => {
      if (typeof unsubActivation === 'function') unsubActivation();
      if (typeof unsubMessage === 'function') unsubMessage();
    };
  }, [
    hasAccess,
    gatewayPorts,
    pendingTestPorts,
    setPortStatusFromActivity,
    startCountdown,
    refreshActivation,
  ]);

  // Calculate next port number to suggest in the Add New Port form.
  // Prefers the first hardware-active-but-unregistered port (based on Port Status Monitor data),
  // falling back to the next sequential slot if none found.
  const getNextPortNumber = useCallback(() => {
    // Combine confirmed ports and pending test ports
    const allPorts = [...gatewayPorts, ...pendingTestPorts];

    // Build a set of already-registered port numbers (normalised to X.0Y format)
    const registeredPortNumbers = new Set(
      allPorts.map(p => p.port_number).filter(Boolean)
    );

    // --- Hardware-first: prefer an active SIM that isn't registered yet ---
    if (hardwareStatusArray.length > 0) {
      for (const slot of hardwareStatusArray) {
        const isActive = slot.active === 1 || slot.st === 3 || slot.st === 4;
        if (!isActive) continue;
        const [portNumStr, slotNumStr] = String(slot.port || '').split('.');
        const p = parseInt(portNumStr, 10);
        const s = parseInt(slotNumStr, 10);
        if (!p || !s || p < 1 || s < 1) continue;
        const portStr = `${p}.${String(s).padStart(2, '0')}`;
        if (!registeredPortNumbers.has(portStr)) {
          return portStr;
        }
      }
    }

    // --- Fallback: next sequential slot ---
    // Extract existing port numbers and parse them (supports both formats)
    const existingPorts = allPorts
      .map(p => p.port_number)
      .filter(p => p)
      .map(p => {
        // Try new format first (1.01, 1.32, etc.)
        const newFormatMatch = p.match(/^(\d+)\.(\d+)$/);
        if (newFormatMatch) {
          const row = parseInt(newFormatMatch[1]);
          const slot = parseInt(newFormatMatch[2]);
          if (row >= 1 && row <= 16 && slot >= 1 && slot <= 32) {
            return { row, slot, original: p };
          }
        }
        // Try old format (1A, 12C, etc.)
        const oldFormatMatch = p.match(/^(\d+)([A-Ha-h])$/);
        if (oldFormatMatch) {
          const row = parseInt(oldFormatMatch[1]);
          const letter = oldFormatMatch[2].toUpperCase();
          const slotMap = { 'A': 1, 'B': 2, 'C': 3, 'D': 4, 'E': 5, 'F': 6, 'G': 7, 'H': 8 };
          const slot = slotMap[letter];
          if (slot && row >= 1 && row <= 16) {
            return { row, slot, original: p };
          }
        }
        return null;
      })
      .filter(p => p !== null)
      .sort((a, b) => {
        if (a.row !== b.row) return a.row - b.row;
        return a.slot - b.slot;
      });

    // Order: For each slot (1-32), go through all rows (1-16): 1.01, 2.01, ..., 16.01, then 1.02, ..., 16.17, ...
    for (let slot = 1; slot <= 32; slot++) {
      for (let row = 1; row <= 16; row++) {
        const portStr = `${row}.${String(slot).padStart(2, '0')}`;
        const exists = existingPorts.some(p => p.row === row && p.slot === slot);
        if (!exists) {
          return portStr;
        }
      }
    }

    return null;
  }, [gatewayPorts, pendingTestPorts, hardwareStatusArray]);

  // Update form data with next port number when ports change
  useEffect(() => {
    if (!editingPort && showAddForm) {
      const nextPort = getNextPortNumber();
      if (nextPort) {
        setFormData(prev => ({
          ...prev,
          port_number: nextPort
        }));
        // Clear any validation errors when auto-filling
        setPortNumberError(null);
      }
    }
  }, [gatewayPorts, pendingTestPorts, editingPort, showAddForm, getNextPortNumber, hardwareStatusArray]);

  // Validate port number (uses util; gateways 1 and 2 allow old format e.g. 1A)
  const validatePortNumber = useCallback((portNumber, excludePortId = null) => {
    const allPorts = [...gatewayPorts, ...pendingTestPorts];
    return validatePortNumberUtil(portNumber, allPorts, excludePortId, gateway?.id);
  }, [gatewayPorts, pendingTestPorts, gateway?.id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'port_number') {
      // Only strip invalid chars on change, don't pad - padding happens on blur
      const cleaned = value.replace(/[^\d.]/g, '');
      setFormData(prev => ({ ...prev, [name]: cleaned }));

      if (cleaned && cleaned.trim()) {
        const validation = validatePortNumber(cleaned, editingPort?.id);
        setPortNumberError(validation.error);
      } else {
        setPortNumberError(null);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handler for sending test message after activation
  const handleSendTestMessage = useCallback(async (portId, phoneNumber) => {
    try {
      setIsSendingTest(true);
      setError(null);
      
      // Normalize recipient
      const digits = (s) => String(s || '').replace(/\D/g, '');
      const normalizeRecipient = (raw) => {
        const trimmed = String(raw || '').trim();
        if (!trimmed) return '';
        if (trimmed.startsWith('+')) return '+' + digits(trimmed);
        return digits(trimmed);
      };
      
      const recipient = normalizeRecipient(phoneNumber);
      if (!recipient) {
        setError('Invalid phone number format. Please check the number and try again.');
        return;
      }

      await api.post('/gateways/me/test-sms', {
        recipient: recipient,
        message: 'Test message from Sim Box - please confirm if you received this.',
      });
      
      // Set test status to 'sent'
      setPortTestStatus(prev => ({
        ...prev,
        [portId]: 'sent'
      }));
      setSuccess('Test message sent! Please check your phone and confirm if you received it.');
    } catch (testErr) {
      console.error('Failed to send test:', testErr);
      setPortTestStatus(prev => ({
        ...prev,
        [portId]: 'failed'
      }));
      const testErrorDetail = formatErrorDetail(testErr.response?.data?.detail) || testErr.message || 'Failed to send test message';
      setError(`Test message failed: ${testErrorDetail}`);
    } finally {
      setIsSendingTest(false);
    }
  }, [formatErrorDetail]);

  // Handler for test confirmation/denial in pending test table
  const handlePortTest = useCallback(async (portId, status) => {
    if (status === 'confirmed') {
      // Move port from pending to main table by refreshing
      setSuccess('Test confirmed for this port! Port has been added to your Sim Box.');
      setPendingTestPorts(prev => prev.filter(p => p.id !== portId));
      setPortTestStatus(prev => {
        const next = { ...prev };
        delete next[portId];
        return next;
      });
      // Clear lastCreatedPort so user can add another port
      if (lastCreatedPort?.id === portId) {
        setLastCreatedPort(null);
      }
      // Refresh ports to include the confirmed port
      await fetchPorts();
      // Update form with next port number
      const nextPort = getNextPortNumber();
      if (nextPort) {
        setFormData(prev => ({
          ...prev,
          port_number: nextPort
        }));
      }
    } else if (status === 'failed') {
      // Delete the port
      try {
        await deleteMyGatewayPort(portId);
        setSuccess('Port deleted as test was not received.');
        setPendingTestPorts(prev => prev.filter(p => p.id !== portId));
        setPortTestStatus(prev => {
          const next = { ...prev };
          delete next[portId];
          return next;
        });
        // Clear lastCreatedPort so user can add another port
        if (lastCreatedPort?.id === portId) {
          setLastCreatedPort(null);
        }
        // Update form with next port number
        const nextPort = getNextPortNumber();
        if (nextPort) {
          setFormData(prev => ({
            ...prev,
            port_number: nextPort
          }));
        }
      } catch (err) {
        const errorDetail = formatErrorDetail(err.response?.data?.detail) || err.message || 'Failed to delete port';
        setError(errorDetail);
      }
    }
  }, [lastCreatedPort, fetchPorts, getNextPortNumber, formatErrorDetail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate port number before submission
    if (!editingPort) {
      const validation = validatePortNumber(formData.port_number);
      if (!validation.valid) {
        setError(validation.error);
        setPortNumberError(validation.error);
        return;
      }
    } else {
      // For editing, validate but allow existing port
      const validation = validatePortNumber(formData.port_number, editingPort.id);
      if (!validation.valid) {
        setError(validation.error);
        setPortNumberError(validation.error);
        return;
      }
    }
    
    // If editing, allow normal update flow
    if (editingPort) {
      try {
        setError(null);
        setSuccess(null);
        setPortNumberError(null);
        setIsSubmitting(true);

        // Prepare port data (network_provider may not be accepted by backend yet)
        const portData = {
          port_number: formData.port_number.trim(),
          assigned_number: formData.assigned_number,
        };
        
        // Include target_user_code if specified and different from current port's user_code
        // Empty string means assign to gateway owner (current logged-in user)
        const newTargetUserCode = formData.target_user_code && formData.target_user_code.trim() 
          ? formData.target_user_code.trim() 
          : (currentUserCode || null); // If empty, use gateway owner's user_code
        
        // Always include target_user_code if it's different from current
        if (newTargetUserCode && newTargetUserCode !== editingPort.user_code) {
          portData.target_user_code = newTargetUserCode;
        }

        await updateMyGatewayPort(editingPort.id, portData);
        setSuccess('Port updated successfully');
        resetForm();
        await fetchPorts();
      } catch (err) {
        const errorDetail = formatErrorDetail(err.response?.data?.detail) || err.message || 'Failed to save port';
        setError(errorDetail);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // For new ports: require completion of previous port workflow before allowing another
    if (pendingTestPorts.length > 0) {
      const pendingPort = pendingTestPorts[pendingTestPorts.length - 1];
      const activationStatus = portActivationStatus[pendingPort.id] || 'not_activated';
      const testStatus = portTestStatus[pendingPort.id];
      
      // Allow adding new port only if:
      // 1. Previous port test was confirmed/failed (workflow complete), OR
      // 2. Previous port hasn't been activated yet (user can add multiple unactivated ports)
      // Otherwise, require completion of activate -> test -> confirm workflow
      if (testStatus === 'sent') {
        setError('Please confirm or deny receipt of the test message for the previous port before adding another.');
        return;
      }
      // If port is activated but test not sent, require sending test first
      if (activationStatus === 'active' && !testStatus) {
        setError('Please send the test message for the previous port before adding another.');
        return;
      }
    }

    try {
      setError(null);
      setSuccess(null);
      setPortNumberError(null);
      setIsSubmitting(true);

      // Prepare port data (network_provider may not be accepted by backend yet)
      const portData = {
        port_number: formData.port_number.trim(),
        assigned_number: formData.assigned_number,
        // network_provider will be sent but may be ignored by backend until schema is updated
        network_provider: formData.network_provider,
        // Include target_user_code if specified (for assigning to other users)
        target_user_code: formData.target_user_code && formData.target_user_code.trim() ? formData.target_user_code.trim() : null,
      };

      const createdPort = await createMyGatewayPort(portData);

      // Store the created port
      setLastCreatedPort(createdPort);
      
      // Add to pending test ports if phone number is provided (user needs to activate first)
      if (formData.assigned_number) {
        // Add to pending test ports (not main table yet)
        setPendingTestPorts(prev => [...prev, createdPort]);
        // Initialize activation status as 'not_activated'
        setPortActivationStatus(prev => ({
          ...prev,
          [createdPort.id]: 'not_activated'
        }));
        const assignMsg = formData.target_user_code ? ` and assigned to user ${formData.target_user_code}` : '';
        setSuccess(`Port ${createdPort.port_number || 'created'} added${assignMsg}! Please activate the port before sending a test message.`);
      } else {
        // No phone number - add directly to main table
        const assignMsg = formData.target_user_code ? ` and assigned to user ${formData.target_user_code}` : '';
        setSuccess(`Port ${createdPort.port_number || 'created'} added${assignMsg} successfully!`);
        await fetchPorts();
      }
      
      // Clear form but keep it open, set next port number
      // Calculate next port including the newly created port
      const allPortsForNext = [...gatewayPorts, ...pendingTestPorts, createdPort];
      const existingPorts = allPortsForNext
        .map(p => p.port_number)
        .filter(p => p && /^\d+\.\d+$/.test(p))
        .map(p => {
          const [row, slot] = p.split('.').map(Number);
          return { row, slot, original: p };
        })
        .sort((a, b) => {
          if (a.row !== b.row) return a.row - b.row;
          return a.slot - b.slot;
        });
      
      let nextPort = null;
      // Order: For each slot (1-32), go through all rows (1-16): 1.01, 2.01, ..., 16.01, then 1.02, ..., 16.17, ...
      for (let slot = 1; slot <= 32; slot++) {
        for (let row = 1; row <= 16; row++) {
          const portStr = `${row}.${String(slot).padStart(2, '0')}`;
          const exists = existingPorts.some(p => p.original === portStr);
          if (!exists) {
            nextPort = portStr;
            break;
          }
        }
        if (nextPort) break;
      }
      
      setFormData({
        port_number: nextPort || '',
        assigned_number: '',
        network_provider: '',
        target_user_code: '',
      });
    } catch (err) {
      const errorDetail = formatErrorDetail(err.response?.data?.detail) || err.message || 'Failed to save port';
      setError(errorDetail);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (port) => {
    setEditingPort(port);
    setFormData({
      port_number: port.port_number || '',
      assigned_number: port.assigned_number || '',
      network_provider: port.network_provider || '',
      target_user_code: port.user_code || '', // Pre-populate with current user_code
    });
    setShowAddForm(true);
  };

  const handleDelete = async (portId) => {
    // Find the port to get its email before deletion
    const portToDelete = gatewayPorts.find(p => p.id === portId);
    const portNumber = portToDelete?.port_number || portId;
    
    if (!window.confirm(`Are you sure you want to delete port ${portNumber}? This will permanently remove the port and its associated data.`)) {
      return;
    }

    try {
      setError(null);
      await deleteMyGatewayPort(portId);
      setSuccess('Port deleted successfully');
      // Refresh both ports and advice passwords
      await Promise.all([
        fetchPorts(),
        fetchAllData()
      ]);
    } catch (err) {
      const errorDetail = formatErrorDetail(err.response?.data?.detail) || err.message || 'Failed to delete port';
      setError(errorDetail);
    }
  };

  const resetForm = useCallback(() => {
    setEditingPort(null);
    setShowAddForm(true); // Keep form always open
    setPortNumberError(null);
    const nextPort = getNextPortNumber();
    setFormData({
      port_number: nextPort || '',
      assigned_number: '',
      network_provider: '',
      target_user_code: '',
    });
  }, [getNextPortNumber]);

  // Handle updating email and site in advice passwords
  const handleUpdateEmail = async (adviceId, newEmail, newSite) => {
    try {
      const updateData = { email: newEmail };
      if (newSite !== undefined) {
        updateData.site = newSite;
      }
      await updateAdvicePasswordMe(adviceId, updateData);
      setSuccess('Email and site updated successfully');
      // Refresh both advice passwords and ports to ensure UI is in sync
      const [updatedAdvice, updatedPorts] = await Promise.all([
        fetchAdvicePasswordsMe().catch(() => advice),
        fetchPorts().catch(() => gatewayPorts)
      ]);
      setAdvice(updatedAdvice || advice);
      setGatewayPorts(updatedPorts || gatewayPorts);
    } catch (err) {
      throw err;
    }
  };

  // Handle updating password in advice passwords
  const handleUpdatePassword = async (adviceId, newPassword) => {
    try {
      await updateAdvicePasswordMe(adviceId, { password: newPassword });
      setSuccess('Password updated successfully');
      const updated = await fetchAdvicePasswordsMe().catch(() => advice);
      setAdvice(updated || advice);
    } catch (err) {
      throw err;
    }
  };

  // Handle updating port number and phone number
  const handleUpdatePort = async (portId, portData) => {
    try {
      await updateMyGatewayPort(portId, portData);
      setSuccess('Port updated successfully');
      await fetchPorts();
    } catch (err) {
      throw err;
    }
  };

  const handleReportProblem = useCallback((port) => {
    // Could open a modal or navigate to a report page
    alert(`Report problem for port ${port.port_number || port.id}`);
  }, []);

  const handleRequestSimConversion = useCallback(async (port) => {
    const updated = await requestSimConversion(port.id);
    setGatewayPorts((prev) =>
      prev.map((p) => (p.id === port.id ? { ...p, ...updated } : p))
    );
    setAssignedPorts((prev) =>
      prev.map((p) => (p.id === port.id ? { ...p, ...updated } : p))
    );
    setSuccess(
      updated?.req_sim_conversion_id
        ? `TT OTP conversion requested (request #${updated.req_sim_conversion_id})`
        : 'TT OTP conversion requested'
    );
    return updated;
  }, []);

  const handleBulkRequestSimConversion = useCallback(async (selectedPorts) => {
    const ids = (selectedPorts || []).map((p) => p.id).filter((id) => id != null);
    if (!ids.length) {
      throw new Error('Select at least one number');
    }
    const result = await requestSimConversionBulk(ids);
    const updatedById = new Map(
      (result?.requested || []).map((p) => [p.id, p])
    );
    if (updatedById.size) {
      setGatewayPorts((prev) =>
        prev.map((p) => (updatedById.has(p.id) ? { ...p, ...updatedById.get(p.id) } : p))
      );
      setAssignedPorts((prev) =>
        prev.map((p) => (updatedById.has(p.id) ? { ...p, ...updatedById.get(p.id) } : p))
      );
    }
    const n = result?.requested?.length || 0;
    const skipped = result?.skipped?.length || 0;
    if (!n && skipped) {
      throw new Error(
        result.skipped[0]?.reason || 'None of the selected numbers could be requested'
      );
    }
    setSuccess(
      skipped
        ? `TT OTP conversion requested for ${n} number(s); ${skipped} skipped${
            result?.request_id ? ` (request #${result.request_id})` : ''
          }`
        : `TT OTP conversion requested for ${n} number(s)${
            result?.request_id ? ` (request #${result.request_id})` : ''
          }`
    );
    return result;
  }, []);

  // Auto-clear messages
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  if (loading) {
    return <div className="container-fluid px-3 py-2">Loading...</div>;
  }

  // Don't render anything if user doesn't have access
  if (!hasAccess) {
    return (
      <div className="container-fluid px-3 py-2">
        <div className="alert alert-info">
          You don't have gateway access. Contact staff to request access.
        </div>
      </div>
    );
  }

  // Always show the form if user has access, even if no gateway or ports exist

  return (
    <div className="container-fluid px-3 py-2" style={{ fontSize: '13px' }}>
      {Array.from(pollingPorts.entries()).map(([portId, { gatewayId: gid, portNumber: pnum }]) => (
        <PollingRunner
          key={portId}
          portId={portId}
          gatewayId={gid}
          portNumber={pnum}
          setPortStatusFromActivity={setPortStatusFromActivity}
          stopActivationPolling={stopActivationPolling}
          refreshActivation={refreshActivation}
        />
      ))}
      {error && <div className="alert alert-danger py-2">{typeof error === 'string' ? error : String(error)}</div>}
      {success && <div className="alert alert-success py-2">{success}</div>}
      
      {/* Show info message if no gateway exists but user has access */}
      {!gateway && gatewayPorts.length === 0 && (
        <div className="alert alert-info py-2 mb-3">
          Your gateway is being set up by staff. You can still add ports below.
        </div>
      )}

      {/* Gateway Details Card - Only show if gateway exists */}
      {gateway && (
        <div className="card mb-3" style={{ backgroundColor: '#f8f9fa' }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0" style={{ fontSize: '18px' }}>
                <span className="badge bg-info me-2">Gateway:</span>
                {gateway.name}
              </h5>
              <span className={`badge ${gateway.is_active ? 'bg-success' : 'bg-secondary'}`}>
                {gateway.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="row g-2" style={{ fontSize: '12px' }}>
              <div className="col-md-3">
                <strong>Host:</strong> {gateway.host}
              </div>
              <div className="col-md-2">
                <strong>Port:</strong> {gateway.port}
              </div>
              <div className="col-md-3">
                <strong>Model:</strong> {gateway.model || 'N/A'}
              </div>
              <div className="col-md-2">
                <strong>Port Count:</strong> {gateway.port_count || 64}
              </div>
              <div className="col-md-2">
                <strong>Total Ports:</strong> {gatewayPorts.length}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Port Status Monitor - always visible, no conditional */}
      <PortStatusMonitorPanel
        gatewayId={gateway?.id}
        gatewayName={gateway?.name || 'My Sim Box'}
        isOpen={portStatusMonitorOpen}
        onToggle={() => setPortStatusMonitorOpen(!portStatusMonitorOpen)}
        onStatusUpdate={setHardwareStatusArray}
      />

      <div className="mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <h5 className="mb-0" style={{ fontSize: '18px' }}>Ports Management</h5>
          {lastSyncTime && (
            <small className="text-muted" style={{ fontSize: '11px' }}>
              Last synced from hardware: {lastSyncTime.toLocaleTimeString()}
            </small>
          )}
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          {/* Prominent hardware sync button */}
          <button
            type="button"
            onClick={handleSyncFromHardware}
            disabled={syncing}
            title={`Pull live state from the SIM box and fix stale port statuses${lastSyncTime ? ` · Last: ${lastSyncTime.toLocaleTimeString()}` : ''}`}
            style={{
              fontWeight: 700,
              fontSize: '13px',
              padding: '0.4rem 0.9rem',
              borderRadius: '0.375rem',
              border: '2px solid #e65c00',
              background: syncing ? '#cc7a00' : 'linear-gradient(135deg,#ff8c00,#e65c00)',
              color: '#fff',
              boxShadow: '0 2px 6px rgba(230,92,0,0.35)',
              cursor: syncing ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {syncing ? (
              <><span className="spinner-border spinner-border-sm me-1" />Syncing…</>
            ) : (
              '⚡ Sync from Hardware'
            )}
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-warning"
            onClick={async () => {
              await loadAssignedPortsForConversion();
              setBulkConversionOpen(true);
            }}
            disabled={eligibleConversionPorts.length === 0}
            title={
              eligibleConversionPorts.length === 0
                ? 'No gateway numbers are eligible for conversion'
                : 'Request TT OTP conversion for multiple numbers'
            }
            style={{ fontWeight: 700, borderWidth: 2 }}
          >
            Bulk convert to TT OTP
            {eligibleConversionPorts.length > 0
              ? ` (${eligibleConversionPorts.length})`
              : ''}
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setPortStatusMonitorOpen(!portStatusMonitorOpen)}
            style={{
              fontWeight: 'bold',
              backgroundColor: portStatusMonitorOpen ? '#1a73e8' : '#ffffff',
              color: portStatusMonitorOpen ? '#fff' : '#1a73e8',
              border: '2px solid #1a73e8',
              padding: '0.4rem 0.9rem',
              borderRadius: '0.375rem',
            }}
          >
            {portStatusMonitorOpen ? '▼ Hide Port Status Monitor' : '▶ Port Status Monitor'}
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => {
              setShowSimGuide(!showSimGuide);
              if (!showSimGuide) setShowAddForm(true);
            }}
            style={{
              fontWeight: 'bold',
              backgroundColor: showSimGuide ? '#dc3545' : '#ffffff',
              color: showSimGuide ? '#fff' : '#dc3545',
              border: '2px solid #dc3545',
              padding: '0.4rem 0.9rem',
              borderRadius: '0.375rem',
            }}
          >
            {showSimGuide ? '▼ Hide SIM Insertion Guide' : '▶ SIM Insertion Guide'}
          </button>
          {lastCreatedPort && (
            <button
              className="btn btn-success btn-sm"
              onClick={() => {
                setLastCreatedPort(null);
                setSuccess('Port confirmed! You can now add another port.');
              }}
            >
              ✓ Confirm Port is Working
            </button>
          )}
          {editingPort && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={resetForm}
            >
              Cancel Edit
            </button>
          )}
        </div>
      </div>

      {showAddForm && (
        <div className="card mb-3" style={{ borderColor: lastCreatedPort ? '#ffc107' : undefined, borderWidth: lastCreatedPort ? '2px' : undefined }}>
          <div className="card-body">
            <h6 className="card-title mb-3">
              {editingPort ? 'Edit Port' : 'Add New Port'}
            </h6>
            <div className="text-center mb-3 d-flex flex-wrap justify-content-center gap-2">
              <button
                className="btn btn-sm"
                onClick={() => setShowSimGuide(!showSimGuide)}
                style={{ 
                  fontSize: '13px', 
                  fontWeight: 'bold',
                  backgroundColor: '#ffffff',
                  color: '#dc3545',
                  border: '2px solid #dc3545',
                  padding: '0.4rem 1rem',
                  borderRadius: '0.375rem'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#dc3545';
                  e.target.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#ffffff';
                  e.target.style.color = '#dc3545';
                }}
              >
                {showSimGuide ? '▼ Hide SIM Insertion Guide' : '▶ Show SIM Insertion Guide'}
              </button>
              {gateway && (
                <button
                  className="btn btn-sm"
                  onClick={() => setPortStatusMonitorOpen(!portStatusMonitorOpen)}
                  style={{ 
                    fontSize: '13px', 
                    fontWeight: 'bold',
                    backgroundColor: '#ffffff',
                    color: '#1a73e8',
                    border: '2px solid #1a73e8',
                    padding: '0.4rem 1rem',
                    borderRadius: '0.375rem'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#1a73e8';
                    e.target.style.color = '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#ffffff';
                    e.target.style.color = '#1a73e8';
                  }}
                >
                  {portStatusMonitorOpen ? '▼ Hide Port Status Monitor' : '▶ Port Status Monitor'}
                </button>
              )}
            </div>
            <form onSubmit={handleSubmit}>
              <div className="row g-2 mb-2">
                <div className="col-md-4">
                  <label className="form-label">Step 1: Phone Number</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="assigned_number"
                    value={formData.assigned_number}
                    onChange={handleInputChange}
                    placeholder="e.g., 447123456789"
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Step 2: Port Number *</label>
                  <input
                    type="text"
                    className={`form-control form-control-sm ${portNumberError ? 'is-invalid' : ''}`}
                    name="port_number"
                    value={formData.port_number}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      const cleaned = cleanPortNumberInput(e.target.value);
                      const formatted = formatPortNumber(cleaned);
                      if (formatted && !formatted.includes('⚠️')) {
                        setFormData(prev => ({ ...prev, port_number: formatted }));
                      }
                    }}
                    placeholder="e.g., 1.01 or 1A (gateways 1–2)"
                    required
                    style={{ 
                      backgroundColor: '#fff',
                      cursor: 'text'
                    }}
                    title="Format: ROW.SLOT (1.01–16.32) or 1A–16H for gateways 1 and 2"
                  />
                  {portNumberError && (
                    <div className="invalid-feedback" style={{ display: 'block', fontSize: '11px' }}>
                      {portNumberError}
                    </div>
                  )}
                  {!portNumberError && formData.port_number && (
                    <small className="text-muted" style={{ fontSize: '11px' }}>
                      Format: Row (1-16).Slot (01-32)
                    </small>
                  )}
                </div>
                <div className="col-md-4">
                  <label className="form-label">Step 3: Network Provider</label>
                  <select
                    className="form-select form-select-sm"
                    name="network_provider"
                    value={formData.network_provider}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Network Provider</option>
                    <option value="EE">EE</option>
                    <option value="Lebara">Lebara</option>
                  </select>
                </div>
              </div>
              <div className="row g-2 mb-2">
                <div className="col-md-12">
                  <label className="form-label">
                    {editingPort ? 'Reassign to User (Optional)' : 'Step 4: Assign to User (Optional)'}
                    {!editingPort && <small className="text-muted ms-2">Leave empty to assign to yourself</small>}
                    {editingPort && <small className="text-muted ms-2">Select a user to reassign this port</small>}
                  </label>
                  <select
                    className="form-select form-select-sm"
                    name="target_user_code"
                    value={formData.target_user_code || ''}
                    onChange={handleInputChange}
                  >
                    {editingPort ? (
                      <>
                        <option value="">Assign to me (gateway owner)</option>
                        {assignableUsers.map((user) => (
                          <option 
                            key={user.user_code} 
                            value={user.user_code}
                          >
                            {user.user_code}{user.user_code === editingPort.user_code ? ' (current)' : ''}
                          </option>
                        ))}
                      </>
                    ) : (
                      <>
                        <option value="">Assign to me (default)</option>
                        {assignableUsers.map((user) => (
                          <option key={user.user_code} value={user.user_code}>
                            {user.user_code}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  <small className="text-muted" style={{ fontSize: '11px' }}>
                    Only users with Slack IDs can be assigned ports. If you don't see a user, they need a Slack ID assigned first.
                  </small>
                </div>
              </div>
              <div className="alert alert-info py-2 mb-2" style={{ fontSize: '12px' }}>
                <strong>Note:</strong> Add one port at a time. After saving, activate the port, then send a test message, and confirm it's working before adding the next one. After confirmation, you can add email, site, and password from the row below or from My Dashboard.
              </div>

              <div className="d-flex gap-2">
                <button 
                  type="submit" 
                  className="btn btn-primary btn-sm"
                  disabled={isSubmitting || (pendingTestPorts.length > 0 && !editingPort) || !!portNumberError}
                  title={
                    portNumberError 
                      ? portNumberError
                      : pendingTestPorts.length > 0 && !editingPort
                      ? 'Please confirm or deny receipt of the test message for the previous port first' 
                      : ''
                  }
                >
                  {isSubmitting ? 'Saving...' : editingPort ? 'Update Port' : 'Add Port'}
                </button>
                {editingPort && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={resetForm}>
                    Cancel
                  </button>
                )}
              </div>

              {/* Expandable SIM Insertion Guide */}
              {showSimGuide && (
                <div className="mt-3 pt-3 border-top alert alert-danger" style={{ backgroundColor: '#f8d7da', borderColor: '#f5c2c7', borderRadius: '0.375rem', padding: '1rem' }}>
                  <h6 className="mb-3" style={{ fontSize: '14px', fontWeight: 'bold' }}>
                    Ejoin Gateway – SIM Insertion Guide
                  </h6>

                  <div className="mb-3">
                    <h6 style={{ fontSize: '13px', fontWeight: 'bold' }}>1. Before You Start</h6>
                    <ul style={{ paddingLeft: '20px', marginBottom: '0' }}>
                      <li>Check the SIM is full size to match the SIM in the images below. Full size SIM pictured in images in step 3.</li>
                      <li>Check the gold part of the SIM is clean and has no scratches.</li>
                    </ul>
                  </div>

                  <div className="mb-3">
                    <h6 style={{ fontSize: '13px', fontWeight: 'bold' }}>2. Understanding the Layout</h6>
                    <p style={{ marginBottom: '0.5rem' }}>
                      The SIMs are laid out in the pattern pictured. Each numbered row can fit 16 SIM cards (slots 1-16) and only one of each row can be active at one time. The pattern can be seen in the photos.
                    </p>
                    <p style={{ marginBottom: '0.5rem' }}>
                      Each row is numbered with a number starting from the bottom and each port within a row follows the pattern pictured with <strong>1.01</strong> being in bottom left, <strong>1.02</strong> being the slot above 1.01 and <strong>1.03</strong> being the slot to the right of 1.01. It follows this pattern all the way to the right of the box until we reach <strong>1.16</strong>.
                    </p>
                    <p style={{ marginBottom: '0.5rem', backgroundColor: '#fff3cd', padding: '0.5rem', borderRadius: '0.25rem', border: '1px solid #ffc107' }}>
                      <strong>⚠️ Port Assignment Order:</strong> When adding new SIMs, ports are automatically assigned in this sequence:
                      <br />• <strong>Slot 1:</strong> <strong>1.01 → 2.01 → 3.01 → ... → 16.01</strong>
                      <br />• <strong>Slot 2:</strong> <strong>1.02 → 2.02 → 3.02 → ... → 16.02</strong>
                      <br />• <strong>And so on:</strong> Continue through all slots 1-16, filling each slot across all rows 1-16 before moving to the next slot.
                    </p>
                    <div className="mb-2">
                      <img 
                        src="/images/labelledBox.jpg" 
                        alt="SIM panel showing labeled port slots" 
                        className="rounded border"
                        style={{ maxWidth: '400px', width: '100%', height: 'auto', display: 'block' }}
                      />
                      <p className="text-muted mt-1 mb-0" style={{ fontSize: '11px' }}>
                        <strong>Photo:</strong> SIM panel layout showing the numbering pattern.
                      </p>
                    </div>
                  </div>

                  <div className="mb-3">
                    <h6 style={{ fontSize: '13px', fontWeight: 'bold' }}>3. SIM Orientation and Insertion</h6>
                    <p style={{ marginBottom: '0.5rem' }}>
                      SIM orientation is pictured in images 2 and 3. The gold contacts for a single row need to always be facing each other as pictured.
                    </p>
                    <ul style={{ paddingLeft: '20px', marginBottom: '1rem' }}>
                      <li>Align the SIM with the opening of that slot, in the orientation described above.</li>
                      <li>Gently slide the SIM straight into the slot along the guides until it stops.</li>
                      <li>The coloured top edge of the SIM should remain visible, in line with the other filled slots.</li>
                      <li>Check that the SIM sits flat and straight and does not wobble loosely.</li>
                    </ul>
                    <div className="mb-2">
                      <img 
                        src="/images/insertingInto1A.jpg" 
                        alt="SIM card held in correct orientation before insertion" 
                        className="rounded border"
                        style={{ maxWidth: '400px', width: '100%', height: 'auto', display: 'block' }}
                      />
                      <p className="text-muted mt-1 mb-0" style={{ fontSize: '11px' }}>
                        <strong>Photo:</strong> SIM held in correct orientation showing gold contacts facing each other.
                      </p>
                    </div>
                    <div className="mb-2">
                      <img 
                        src="/images/InsertingInto1B.jpg" 
                        alt="SIM card fully inserted and seated" 
                        className="rounded border"
                        style={{ maxWidth: '400px', width: '100%', height: 'auto', display: 'block' }}
                      />
                      <p className="text-muted mt-1 mb-0" style={{ fontSize: '11px' }}>
                        <strong>Photo:</strong> SIM fully inserted and seated, aligned with neighbouring SIMs.
                      </p>
                    </div>
                  </div>

                  <div className="mb-3">
                    <h6 style={{ fontSize: '13px', fontWeight: 'bold' }}>4. Removing a SIM</h6>
                    <ul style={{ paddingLeft: '20px', marginBottom: '0' }}>
                      <li>Grip the visible coloured portion of the SIM.</li>
                      <li>Pull the SIM straight out of the slot without twisting.</li>
                    </ul>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Pending Test Table */}
      {pendingTestPorts.length > 0 && (
        <div className="card mb-3" style={{ borderColor: '#ffc107', borderWidth: '2px', backgroundColor: '#fff9e6' }}>
          <div className="card-body">
            <h6 className="card-title mb-3">
              <span className="badge bg-warning text-dark me-2">⚠️</span>
              Pending Test Confirmation
            </h6>
            <div className="table-responsive">
              <table className="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>Port</th>
                    <th>Phone Number</th>
                    <th>Network Provider</th>
                    <th>Activation Status</th>
                    <th>Test Status</th>
                    <th>Recent Messages</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingTestPorts.map((port) => {
                    const testStatus = portTestStatus[port.id] || null;
                    const activationStatus = portActivationStatus[port.id] || 'not_activated';
                    const messages = portMessages[port.id] || [];
                    const portStatusData = portStatus[port.id];
                    const isActivating = activationStatus === 'activating' || activationStatus === 'queued';
                    const isActive = activationStatus === 'active' || portStatusData?.status === 'active';
                    
                    return (
                      <tr key={port.id} style={{ backgroundColor: '#ffeb3b' }}>
                        <td><strong>{port.port_number || '—'}</strong></td>
                        <td>{port.assigned_number || '—'}</td>
                        <td>{port.network_provider || '—'}</td>
                        <td>
                          {activationStatus === 'not_activated' && (
                            <span className="badge bg-secondary">Not Activated</span>
                          )}
                          {isActivating && (
                            <span className="badge bg-warning text-dark">
                              {activationStatus === 'activating' ? 'Activating...' : 'Queued'}
                            </span>
                          )}
                          {isActive && (
                            <span className="badge bg-success">Active</span>
                          )}
                        </td>
                        <td>
                          {testStatus === 'sent' && (
                            <span className="badge bg-info">Test Sent - Awaiting Confirmation</span>
                          )}
                          {testStatus === 'failed' && (
                            <span className="badge bg-danger">Test Failed</span>
                          )}
                          {!testStatus && isActive && (
                            <span className="badge bg-secondary">Ready to Test</span>
                          )}
                          {!testStatus && !isActive && (
                            <span className="badge bg-secondary">Activate First</span>
                          )}
                        </td>
                        <td>
                          <MessageCarousel
                            messages={messages}
                            portNumber={port.assigned_number || port.port_number || '—'}
                          />
                        </td>
                        <td>
                          {activationStatus === 'not_activated' && shouldShowActivate(port) && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleActivatePort(port.id, port.assigned_number)}
                              style={{ fontSize: '0.75rem' }}
                            >
                              Activate Port
                            </button>
                          )}
                          {activationStatus === 'not_activated' && !shouldShowActivate(port) && (
                            <span className="badge bg-secondary" title={isSimConvertedYes(port) ? 'SIM already converted' : 'Service number'}>
                              {isSimConvertedYes(port) ? 'Converted' : 'Service'}
                            </span>
                          )}
                          {isActivating && (
                            <span className="text-muted small">
                              {activationStatus === 'activating' ? 'Activating...' : 'Queued for activation'}
                            </span>
                          )}
                          {isActive && !testStatus && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleSendTestMessage(port.id, port.assigned_number)}
                              disabled={isSendingTest}
                              style={{ fontSize: '0.75rem' }}
                            >
                              {isSendingTest ? 'Sending...' : 'Send Test Message'}
                            </button>
                          )}
                          {testStatus === 'sent' && (
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handlePortTest(port.id, 'confirmed')}
                                style={{ fontSize: '0.75rem' }}
                              >
                                ✓ Received
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handlePortTest(port.id, 'failed')}
                                style={{ fontSize: '0.75rem' }}
                              >
                                ✗ Not Received
                              </button>
                            </div>
                          )}
                          {testStatus === 'failed' && (
                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => handleSendTestMessage(port.id, port.assigned_number)}
                              disabled={isSendingTest}
                              style={{ fontSize: '0.75rem' }}
                            >
                              {isSendingTest ? 'Sending...' : 'Retry Test'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Gateway Ports Table */}
      <GatewayPortsTable
        gatewayName={gateway?.name || 'My Sim Box'}
        ports={gatewayPorts}
        advicePasswords={advice}
        portStatus={portStatus}
        portMessages={portMessages}
        portCountdowns={portCountdowns}
        clearPortMessage={clearPortMessage}
        getButtonState={getButtonState}
        getStatusBadge={getStatusBadge}
        onActivatePort={handleActivatePort}
        onReportProblem={handleReportProblem}
        onEditPort={handleEdit}
        onDeletePort={handleDelete}
        onUpdateEmail={handleUpdateEmail}
        onUpdatePassword={handleUpdatePassword}
        onUpdatePort={handleUpdatePort}
        onSendTest={handlePortTest}
        testStatus={portTestStatus}
        lastCreatedPortId={lastCreatedPort?.id} // Only show test button on most recently added port
        isStaff={false}
        sites={sites}
        showUserCode={true} // Show User Code column only on My Sim Box page
        assignableUsers={assignableUsers} // Pass assignable users for User Code editing
        currentUserCode={currentUserCode} // Pass current user code for reassignment
        onRequestSimConversion={handleRequestSimConversion}
      />
      <ConvertToTtOtpModal
        open={bulkConversionOpen}
        bulk
        ports={eligibleConversionPorts}
        isStaff={false}
        onClose={() => setBulkConversionOpen(false)}
        onConfirm={handleBulkRequestSimConversion}
      />
    </div>
  );
}

export default UserGatewayPage;

